import { AfterContentInit, Directive, ElementRef, Input, OnChanges, Renderer2 } from '@angular/core';
import { environment } from '@y42/environment';
import { Y42RouterService } from '@y42/feature/routing';
import { getSafeString } from '@y42/util/safe-string';
import { INTERCOM_ATTRIBUTE } from './intercom.model';

@Directive({
  selector: '[intercomTarget]',
})
export class IntercomTargetDirective implements AfterContentInit, OnChanges {
  constructor(
    private readonly elRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly routerService: Y42RouterService,
  ) {}

  @Input() intercomTarget? = '';

  ngAfterContentInit() {
    this.generateIdentifier();
  }

  ngOnChanges() {
    this.generateIdentifier();
  }

  private generateIdentifier() {
    if (this.intercomTarget) {
      this.setAttribute(this.intercomTarget);
      return;
    }

    const ele = this.elRef.nativeElement as HTMLElement;
    const elementIdentifier = this.getIdentifier(ele);

    if (!environment.production_build && !elementIdentifier) {
      console.warn(`[IntercomTarget]: element could not be uniquely identified`, ele);
      this.renderer.setStyle(ele, `border`, `1px dashed red`);
      return;
    }

    const target = getSafeString(`${this.routerService.urlWithoutParams}-${elementIdentifier}`)
      .toLowerCase()
      .replace(/--/g, '-');
    this.setAttribute(target);
  }

  private setAttribute(target: string) {
    this.renderer.setAttribute(this.elRef.nativeElement, INTERCOM_ATTRIBUTE, target || '');
  }

  /**
   * Recursively traverses the element's children and returns an identifying string
   */
  private getIdentifier(node: Node): string {
    if (node.textContent?.trim()) {
      return node.textContent.trim();
    }

    const children = Array.from(node.childNodes);
    const childWithID = children.find((child) => this.getIdentifier(child));

    if (childWithID) {
      return this.getIdentifier(childWithID);
    }

    return '';
  }
}
